import React from "react";
import Icon from "@mdi/react";
import "../inward-styles.css";
import {
  mdiMagnify,
  mdiChevronDown,
  mdiPlus,
  mdiChevronUp,
  mdiAlertOutline,
  mdiSwapHorizontal,
  mdiCloseCircleOutline,
  mdiClose,
  mdiHomeOutline,
} from "@mdi/js";
import { fetchGet } from "../../content_panel/services/serviceClient";
import PaginationWarehouse from "../inward/PaginationWarehouse";
import LoadingWidget from "../../../../context_providers/modal/loader";
import { useEffect, useState } from "react";
import OutwardOrdersTable from "../outward/OutwardOrdersTable";
import MultiSelectDropdown from "../common-components/MultiSelectDropdown";
import ItemSearchTagInput from "../common-components/ItemSearchTagInput";
import {
  fetchExportOutwardByEmail,
  useWarehouseOutward,
  WarehouseOutwardProvider,
} from "../contexts/OutwardContext";
import { mdiEmailOutline } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { useHistory, useLocation } from "react-router-dom";
import { urls } from "../../../../utils/urls";
import OutwardViewModal from "./stock_transfer_view_modal";
import WarehouseSearchFieldByNameID from "../common-components/WarehouseSearchFieldByNameID";
import { useWarehouseViewOnly } from "../hooks/useWarehouseViewOnly";

type TabType =
  | "st-regular"
  | "st-returnable"
  | "st-foc"
  | "st-replacement"
  | "st-reverse-dc"
  | "st-rejected"
  | "st-cancelled";

const TAB_KEYS: TabType[] = [
  "st-regular",
  "st-returnable",
  "st-foc",
  "st-replacement",
  "st-reverse-dc",
  "st-rejected",
  "st-cancelled",
];

const resolveTabFromSearch = (search: string): TabType => {
  const t = new URLSearchParams(search).get("tab");
  return (TAB_KEYS as string[]).includes(t ?? "")
    ? (t as TabType)
    : "st-regular";
};

const WarehouseStockTransferWrapper = () => {
  const location = useLocation();
  const initialTab = resolveTabFromSearch(location.search);
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  return (
    <WarehouseOutwardProvider
      config={{
        initialType: "ST",
        initialTab: initialTab,
        storageKey: "warehouse:stock-transfer:filters:v2",
      }}
    >
      <WarehouseStockTransferPage
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    </WarehouseOutwardProvider>
  );
};

export default WarehouseStockTransferWrapper;

// ================= PAGE =================

const statusOptions = [
  "Draft",
  "HW Approval Pending",
  "Business Approval Pending",
  "ST FG Approval Pending",
  "Device Allocation Pending",
  "Material Picking Pending",
  "Doc Pending",
  "Ready To Dispatch",
  "Dispatched",
];

interface Props {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
}

const WarehouseStockTransferPage: React.FC<Props> = ({
  activeTab,
  setActiveTab,
}) => {
  const {
    outwardList,
    isLoading,
    filters,
    setFilters,
    page,
    setPage,
    pagination,
    statusCount,
    modals,
    selectedOutward,
    closeModal,
  } = useWarehouseOutward();

  const history = useHistory();
  const location = useLocation();
  const isWarehouseViewOnly = useWarehouseViewOnly();
  const [showFilters, setShowFilters] = useState(window.innerWidth >= 768);
  const [showViewOnlyModal, setShowViewOnlyModal] = useState(false);
  const [cascade, setCascade] = useState(false);
  const [cascadeHistory, setCascadeHistory] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { showToast } = useToast();

  const [pendingSTs, setPendingSTs] = useState<any[]>([]);
  const [pendingExpanded, setPendingExpanded] = useState(true);
  const [pendingShowAll, setPendingShowAll] = useState(false);

  useEffect(() => {
    const fetchPendingSTs = async () => {
      try {
        const res = await fetchGet(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/get-warehouse-outwards?type=HOME_REQUEST&status=Pending&all=true&page=1`,
        );
        if (res.success) {
          const all = res.data ?? [];
          setPendingSTs(
            all.filter((st: any) => st.created_from === "home_app"),
          );
        }
      } catch (e) {
        console.error("Failed to fetch pending STs from Home App", e);
      }
    };
    fetchPendingSTs();
  }, []);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await fetchExportOutwardByEmail({
        search: filters.search,
        status: filters.status?.length ? filters.status.join(",") : undefined,
        type: filters.type?.length ? filters.type.join(",") : undefined,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        fromWarehouse: filters.fromWarehouse,
        toWarehouse: filters.toWarehouse,
        itemSearch: filters.itemSearch,
        tab: filters.tab || activeTab,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        includeDispatchedDate: true,
      });
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: res.message || "Export will be emailed shortly.",
        });
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to schedule export",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to schedule export",
      });
    } finally {
      setExporting(false);
    }
  };

  // ================= RESPONSIVE =================

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setShowFilters(!mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ================= TAB → FILTER SYNC =================

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      tab: activeTab,
    }));
    setPage(1);
  }, [activeTab, setFilters, setPage]);

  return (
    <div id="warehouse-stock-transfer-list-screen" className="wh-mobile-shrink">
      <main className="p-4 md:p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-wrap justify-between items-center gap-3">
            <h1 className="text-3xl font-bold border-l-4 border-myPrimaryColor pl-2">
              Stock Transfers
            </h1>
            <button
              id="warehouse-stock-transfer-list-export-email-btn"
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Icon path={mdiEmailOutline} size={0.8} />
              {exporting ? "Scheduling…" : "Export by Email"}
            </button>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-4">
            {[
              {
                label: "Warehouse Yet To Be Assigned",
                value: statusCount["WH Yet To Be Assigned"] || 0,
                border: "border-yellow-800",
              },
              {
                label: "ST FG Approval Pending",
                value: statusCount["ST FG Approval Pending"] || 0,
                border: "border-lime-400",
              },
              {
                label: "Device Allocation Pending",
                value: statusCount["Device Allocation Pending"] || 0,
                border: "border-red-500",
              },
              {
                label: "Material Picking Pending",
                value: statusCount["Material Picking Pending"] || 0,
                border: "border-cyan-500",
              },
              {
                label: "Doc Pending",
                value: statusCount["Doc Pending"] || 0,
                border: "border-purple-500",
              },
              {
                label: "Ready",
                value: statusCount["Ready To Dispatch"] || 0,
                border: "border-green-500",
              },
              {
                label: "Dispatched",
                value: statusCount.Dispatched || 0,
                border: "border-blue-700",
              },
              {
                label: "Rejected",
                value: statusCount.Rejected || 0,
                border: "border-rose-600",
              },
              {
                label: "Cancelled",
                value: statusCount.Cancelled || 0,
                border: "border-gray-500",
              },
              {
                label: "Returned",
                value: statusCount.Returned || 0,
                border: "border-orange-500",
              },
            ].map(({ label, value, border }) => (
              <div
                key={label}
                className={`rounded-lg border-l-4 bg-white shadow-sm ${border}`}
              >
                <div className="p-4">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold">{value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 bg-blue-50 p-2 rounded-md mb-4">
            {[
              { key: "st-regular", label: "Regular" },
              { key: "st-returnable", label: "Returnable" },
              { key: "st-foc", label: "FoC" },
              { key: "st-replacement", label: "Replacement" },
              { key: "st-reverse-dc", label: "Reverse DC" },
              { key: "st-rejected", label: "Rejected ST" },
              { key: "st-cancelled", label: "Cancelled" },
            ].map((t) => (
              <button
                key={t.key}
                id={`warehouse-stock-transfer-list-tab-${t.key}`}
                onClick={() => {
                  const key = t.key as TabType;
                  setActiveTab(key);
                  const params = new URLSearchParams(location.search);
                  params.set("tab", key);
                  history.replace({
                    pathname: location.pathname,
                    search: params.toString(),
                  });
                }}
                className={`px-4 py-2 rounded text-sm font-bold transition
                  ${
                    activeTab === t.key
                      ? "bg-white shadow-sm text-black"
                      : "text-gray-600 hover:bg-white/60"
                  }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Pending STs from Home App */}
          {activeTab === "st-regular" && pendingSTs.length > 0 && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 mb-4">
              <div
                id="warehouse-stock-transfer-list-pending-sts-toggle"
                className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
                onClick={() => setPendingExpanded((v) => !v)}
              >
                <div className="flex items-center gap-2">
                  <Icon
                    path={mdiHomeOutline}
                    size={0.8}
                    className="text-yellow-700"
                  />
                  <span className="text-sm font-semibold text-yellow-800">
                    Pending Requests from Home App
                  </span>
                  <span className="text-xs bg-yellow-200 text-yellow-900 rounded-full px-2 py-0.5 font-medium">
                    {pendingSTs.length}
                  </span>
                </div>
                <Icon
                  path={pendingExpanded ? mdiChevronUp : mdiChevronDown}
                  size={0.8}
                  className="text-yellow-700"
                />
              </div>

              {pendingExpanded && (
                <div className="px-4 pb-4">
                  <table className="w-full text-xs divide-y divide-yellow-200">
                    <thead>
                      <tr className="text-yellow-700 text-left">
                        <th className="py-2 font-medium">Request ID</th>
                        <th className="py-2 font-medium">To Warehouse</th>
                        <th className="py-2 font-medium">Vendor</th>
                        <th className="py-2 font-medium">Items</th>
                        <th className="py-2 font-medium">Raised On</th>
                        <th className="py-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-yellow-100">
                      {(pendingShowAll
                        ? pendingSTs
                        : pendingSTs.slice(0, 5)
                      ).map((st: any) => (
                        <React.Fragment key={st.id}>
                          <tr className="hover:bg-yellow-100">
                            <td className="py-2 pr-4 font-mono">{st.id}</td>
                            <td className="py-2 pr-4">
                              {st.to_warehouse_name ||
                                st.to_warehouse_id ||
                                "—"}
                            </td>
                            <td className="py-2 pr-4">
                              {st.contact_name || "—"}
                            </td>
                            <td className="py-2 pr-4">
                              {st.items?.length ?? 0} item(s)
                            </td>
                            <td className="py-2 pr-4">
                              {st.created_at
                                ? new Date(st.created_at).toLocaleDateString(
                                    "en-IN",
                                  )
                                : "—"}
                            </td>
                            <td className="py-2">
                              <button
                                id={`warehouse-stock-transfer-list-pending-st-create-btn-${st.id}`}
                                className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                                onClick={() =>
                                  history.push(
                                    `${urls.warehouse_panel}/create-stock-transfer?pendingId=${st.id}`,
                                  )
                                }
                              >
                                Create ST
                              </button>
                            </td>
                          </tr>
                          {st.items?.length > 0 && (
                            <tr>
                              <td colSpan={6} className="pb-2 pt-0">
                                <div className="max-h-20 overflow-y-auto rounded border border-yellow-200 bg-yellow-50/60 px-2 py-1 flex flex-wrap gap-x-4 gap-y-1">
                                  {st.items.map((item: any, idx: number) => (
                                    <span
                                      key={idx}
                                      className="text-xs text-yellow-900 whitespace-nowrap"
                                    >
                                      <span className="font-medium">
                                        {item.inventoryItemCode || "—"}
                                      </span>
                                      {item.itemName && (
                                        <span className="text-yellow-700">
                                          {" "}
                                          · {item.itemName}
                                        </span>
                                      )}
                                      {item.requestedQty && (
                                        <span className="text-yellow-600">
                                          {" "}
                                          ×{item.requestedQty}
                                        </span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>

                  {pendingSTs.length > 5 && (
                    <button
                      id="warehouse-stock-transfer-list-pending-sts-show-more-btn"
                      className="mt-2 text-xs text-yellow-700 underline"
                      onClick={() => setPendingShowAll((v) => !v)}
                    >
                      {pendingShowAll
                        ? "Show less"
                        : `Show ${pendingSTs.length - 5} more`}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Filters Toggle (only regular tab) */}
          {activeTab === "st-regular" && (
            <div className="flex items-center justify-between mb-4">
              <div
                id="warehouse-stock-transfer-list-filters-toggle"
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => setShowFilters((p) => !p)}
              >
                <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  Filters
                  <Icon
                    path={showFilters ? mdiChevronUp : mdiChevronDown}
                    size={0.8}
                  />
                </div>
              </div>

              {(filters.search ||
                filters.status?.length ||
                filters.fromDate ||
                filters.toDate ||
                filters.fromWarehouse ||
                filters.toWarehouse ||
                filters.itemSearch) && (
                <button
                  id="warehouse-stock-transfer-list-clear-filters-btn"
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      search: "",
                      status: [],
                      fromDate: "",
                      toDate: "",
                      fromWarehouse: "",
                      toWarehouse: "",
                      itemSearch: "",
                      page: 1,
                    }))
                  }
                  className="text-sm font-semibold text-red-600 hover:text-red-700 flex items-center gap-1 transition-colors"
                >
                  <Icon path={mdiCloseCircleOutline} size={0.6} />
                  Clear All Filters
                </button>
              )}
            </div>
          )}

          {/* Filters */}
          {showFilters && (
            <div className="rounded-lg border border-gray-300 shadow-sm bg-white mb-4">
              {/* ROW 1 */}
              <div className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 flex items-center gap-2">
                    <div className="relative flex-1">
                      <Icon
                        path={mdiMagnify}
                        className="absolute left-3 top-1/3 -translate-y-1/2 h-5 w-5 opacity-60"
                      />

                      <input
                        id="warehouse-stock-transfer-list-search-input"
                        className="h-10 w-full rounded-md border px-3 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Search by Order ID, Tracking ID or Complaint ID..."
                        value={filters.search || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            search: e.target.value,
                            page: 1,
                          }))
                        }
                      />
                    </div>

                    {filters.search && (
                      <button
                        id="warehouse-stock-transfer-list-search-clear-btn"
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            search: "",
                            page: 1,
                          }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Clear Search"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>

                  <MultiSelectDropdown
                    id="warehouse-stock-transfer-list-status-filter"
                    placeholder="All Status"
                    values={filters.status || []}
                    onChange={(next) =>
                      setFilters((prev) => ({
                        ...prev,
                        status: next,
                        page: 1,
                      }))
                    }
                    options={statusOptions.map((s) => ({
                      value: s,
                      label: s,
                    }))}
                  />

                  {/* Create Button */}
                  <button
                    id="warehouse-stock-transfer-list-create-st-btn"
                    onClick={() =>
                      isWarehouseViewOnly
                        ? setShowViewOnlyModal(true)
                        : history.push(
                            urls.warehouse_panel + "/create-stock-transfer",
                          )
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    <Icon path={mdiPlus} size={0.8} /> Create ST
                  </button>
                </div>
              </div>

              {/* ROW 2 */}
              <div className="p-4 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      id="warehouse-stock-transfer-list-from-date-input"
                      type="date"
                      value={filters.fromDate || ""}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          fromDate: e.target.value,
                          page: 1,
                        }))
                      }
                      className="h-10 w-full rounded-md border px-3 text-sm"
                    />

                    {filters.fromDate && (
                      <button
                        id="warehouse-stock-transfer-list-from-date-clear-btn"
                        onClick={() =>
                          setFilters((p) => ({
                            ...p,
                            fromDate: "",
                            page: 1,
                          }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="warehouse-stock-transfer-list-to-date-input"
                      type="date"
                      value={filters.toDate || ""}
                      onChange={(e) =>
                        setFilters((p) => ({
                          ...p,
                          toDate: e.target.value,
                          page: 1,
                        }))
                      }
                      className="h-10 w-full rounded-md border px-3 text-sm"
                    />

                    {filters.toDate && (
                      <button
                        id="warehouse-stock-transfer-list-to-date-clear-btn"
                        onClick={() =>
                          setFilters((p) => ({
                            ...p,
                            toDate: "",
                            page: 1,
                          }))
                        }
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Icon path={mdiCloseCircleOutline} size={0.7} />
                      </button>
                    )}
                  </div>

                  {/* From Warehouse */}
                  <WarehouseSearchFieldByNameID
                    id="warehouse-stock-transfer-list-from-warehouse-input"
                    placeholder="From Warehouse (Name)"
                    value={filters.fromWarehouse || ""}
                    onChange={(val) =>
                      setFilters((p) => ({
                        ...p,
                        fromWarehouse: val,
                        page: 1,
                      }))
                    }
                    onClear={() =>
                      setFilters((p) => ({
                        ...p,
                        fromWarehouse: "",
                        page: 1,
                      }))
                    }
                  />

                  {/* To Warehouse */}
                  <WarehouseSearchFieldByNameID
                    id="warehouse-stock-transfer-list-to-warehouse-input"
                    placeholder="To Warehouse (Name)"
                    value={filters.toWarehouse || ""}
                    onChange={(val) =>
                      setFilters((p) => ({
                        ...p,
                        toWarehouse: val,
                        page: 1,
                      }))
                    }
                    onClear={() =>
                      setFilters((p) => ({
                        ...p,
                        toWarehouse: "",
                        page: 1,
                      }))
                    }
                  />
                </div>
              </div>

              {/* ROW 3 */}
              <div className="p-4 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 items-center">
                  {/* 50% */}
                  <div className="md:col-span-2">
                    <ItemSearchTagInput
                      id="warehouse-stock-transfer-list-item-search-input"
                      value={filters.itemSearch || ""}
                      onChange={(next) =>
                        setFilters((p) => ({
                          ...p,
                          itemSearch: next,
                          page: 1,
                        }))
                      }
                    />
                  </div>

                  <div className="inline-flex items-center gap-2 h-10 select-none">
                    <input
                      id="warehouse-stock-transfer-list-expand-items-checkbox"
                      type="checkbox"
                      checked={cascade}
                      onChange={(e) => setCascade(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />

                    <span className="text-sm text-gray-700">Expand items</span>
                  </div>

                  <div className="inline-flex items-center gap-2 h-10 select-none">
                    <input
                      id="warehouse-stock-transfer-list-expand-history-checkbox"
                      type="checkbox"
                      checked={cascadeHistory}
                      onChange={(e) => setCascadeHistory(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />

                    <span className="text-sm text-gray-700">
                      Expand history
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rejected Info */}
          {activeTab === "st-rejected" && (
            <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-100 p-3">
              <Icon path={mdiAlertOutline} size={1} />

              <div className="text-sm">
                <p className="font-semibold">
                  Rejected and Cancelled ST requests
                </p>
                <p className="mt-1">
                  Only rejected Regular ST can be edited and resubmitted.
                  Returnable and Replacement ST are cancelled directly by
                  finance rejection.
                </p>
              </div>
            </div>
          )}

          {/* Regular Table */}
          {activeTab === "st-regular" && outwardList.length > 0 && (
            <>
              <OutwardOrdersTable
                updatesAllowed={false}
                title={"Stock Transfer Orders"}
                icon={mdiSwapHorizontal}
                cascade={cascade}
                cascadeHistory={cascadeHistory}
              />

              <PaginationWarehouse
                id="warehouse-stock-transfer-list-regular-pagination"
                page={page}
                totalPages={pagination.totalPages}
                onPageChange={(p) => {
                  if (p >= 1 && p <= pagination.totalPages) setPage(p);
                }}
              />
            </>
          )}

          {activeTab === "st-regular" && !outwardList.length && !isLoading && (
            <p className="text-center text-muted-foreground mt-10">
              No regular stock transfer orders found.
            </p>
          )}

          {/* Returnable Table */}
          {activeTab === "st-returnable" && outwardList.length > 0 && (
            <>
              <OutwardOrdersTable
                updatesAllowed={false}
                title={"Stock Transfer Orders"}
                icon={mdiSwapHorizontal}
                showReturnableOverdueTag={true}
                cascade={cascade}
                cascadeHistory={cascadeHistory}
              />

              <PaginationWarehouse
                id="warehouse-stock-transfer-list-returnable-pagination"
                page={page}
                totalPages={pagination.totalPages}
                onPageChange={(p) => {
                  if (p >= 1 && p <= pagination.totalPages) setPage(p);
                }}
              />
            </>
          )}

          {activeTab === "st-returnable" &&
            !outwardList.length &&
            !isLoading && (
              <p className="text-center text-muted-foreground mt-10">
                No returnable stock transfer orders found.
              </p>
            )}

          {/* FoC Table */}
          {activeTab === "st-foc" && outwardList.length > 0 && (
            <>
              <OutwardOrdersTable
                updatesAllowed={false}
                title={"Stock Transfer Orders"}
                icon={mdiSwapHorizontal}
                cascade={cascade}
                cascadeHistory={cascadeHistory}
              />

              <PaginationWarehouse
                id="warehouse-stock-transfer-list-foc-pagination"
                page={page}
                totalPages={pagination.totalPages}
                onPageChange={(p) => {
                  if (p >= 1 && p <= pagination.totalPages) setPage(p);
                }}
              />
            </>
          )}

          {activeTab === "st-foc" && !outwardList.length && !isLoading && (
            <p className="text-center text-muted-foreground mt-10">
              No FoC stock transfer orders found.
            </p>
          )}

          {/* Replacement Table */}
          {activeTab === "st-replacement" && outwardList.length > 0 && (
            <>
              <OutwardOrdersTable
                updatesAllowed={false}
                title={"Stock Transfer Orders"}
                icon={mdiSwapHorizontal}
                cascade={cascade}
                cascadeHistory={cascadeHistory}
              />

              <PaginationWarehouse
                id="warehouse-stock-transfer-list-replacement-pagination"
                page={page}
                totalPages={pagination.totalPages}
                onPageChange={(p) => {
                  if (p >= 1 && p <= pagination.totalPages) setPage(p);
                }}
              />
            </>
          )}

          {activeTab === "st-replacement" &&
            !outwardList.length &&
            !isLoading && (
              <p className="text-center text-muted-foreground mt-10">
                No replacement stock transfer orders found.
              </p>
            )}

          {/* Reverse DC Table */}
          {activeTab === "st-reverse-dc" && outwardList.length > 0 && (
            <>
              <OutwardOrdersTable
                updatesAllowed={false}
                title={"Stock Transfer Orders"}
                icon={mdiSwapHorizontal}
                cascade={cascade}
                cascadeHistory={cascadeHistory}
              />

              <PaginationWarehouse
                id="warehouse-stock-transfer-list-reverse-dc-pagination"
                page={page}
                totalPages={pagination.totalPages}
                onPageChange={(p) => {
                  if (p >= 1 && p <= pagination.totalPages) setPage(p);
                }}
              />
            </>
          )}

          {activeTab === "st-reverse-dc" &&
            !outwardList.length &&
            !isLoading && (
              <p className="text-center text-muted-foreground mt-10">
                No reverse DC stock transfer orders found.
              </p>
            )}

          {/* Rejected Table */}
          {activeTab === "st-rejected" && outwardList.length > 0 && (
            <>
              <OutwardOrdersTable
                updatesAllowed={!isWarehouseViewOnly}
                title={"Rejected Stock Transfers"}
                subTitle={"Orders needing revision before proceeding"}
                icon={mdiCloseCircleOutline}
                cascade={cascade}
                cascadeHistory={cascadeHistory}
              />

              <PaginationWarehouse
                id="warehouse-stock-transfer-list-rejected-pagination"
                page={page}
                totalPages={pagination.totalPages}
                onPageChange={(p) => {
                  if (p >= 1 && p <= pagination.totalPages) setPage(p);
                }}
              />
            </>
          )}

          {activeTab === "st-rejected" && !outwardList.length && !isLoading && (
            <p className="text-center text-muted-foreground mt-10">
              No rejected stock transfer orders found.
            </p>
          )}

          {/* Cancelled Table */}
          {activeTab === "st-cancelled" && outwardList.length > 0 && (
            <>
              <OutwardOrdersTable
                updatesAllowed={false}
                title={"Cancelled Stock Transfers"}
                icon={mdiCloseCircleOutline}
                cascade={cascade}
              />

              <PaginationWarehouse
                id="warehouse-stock-transfer-list-cancelled-pagination"
                page={page}
                totalPages={pagination.totalPages}
                onPageChange={(p) => {
                  if (p >= 1 && p <= pagination.totalPages) setPage(p);
                }}
              />
            </>
          )}

          {activeTab === "st-cancelled" &&
            !outwardList.length &&
            !isLoading && (
              <p className="text-center text-muted-foreground mt-10">
                No cancelled stock transfer orders found.
              </p>
            )}
        </div>
      </main>

      {/* View Modal */}
      {selectedOutward && (
        <OutwardViewModal
          open={modals.view}
          onClose={() => closeModal("view")}
          outward={selectedOutward}
        />
      )}

      {isLoading && <LoadingWidget />}

      {showViewOnlyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            id="warehouse-stock-transfer-list-view-only-modal-overlay-close"
            type="button"
            className="absolute inset-0 bg-black bg-opacity-50"
            aria-label="Close"
            onClick={() => setShowViewOnlyModal(false)}
          />

          <div
            id="warehouse-stock-transfer-list-view-only-modal"
            className="relative z-10 w-full max-w-xl rounded-2xl border bg-white p-6 shadow-lg wh-modal-xs max-h-[90vh] overflow-y-auto"
          >
            <button
              id="warehouse-stock-transfer-list-view-only-modal-close-btn"
              type="button"
              className="absolute right-4 top-4 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close"
              onClick={() => setShowViewOnlyModal(false)}
            >
              <Icon path={mdiClose} size={0.8} />
            </button>

            <h2 className="text-lg font-bold text-red-500 flex flex-row gap-2 ">
              <span>
                <Icon path={mdiAlertOutline} size={1} />
              </span>
              View-only access
            </h2>
            <p className="mt-2 text-base pt-4 pb-4 text-red-400">
              You have Warehouse view-only permission, so creating Stock
              Transfers is disabled.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
